import React, {useState} from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import {Text, TextInput} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {onboardingColors} from '../../theme/onboardingColors';
import {useRegistrationStore} from '../../store/useRegistrationStore';
import StepIndicator from '../../components/StepIndicator';

const schema = yup.object({
  ownerName: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
});

const RegisterStep1Screen = ({navigation}) => {
  const {t} = useTranslation();
  const {ownerName, email, setField} = useRegistrationStore();

  const {
    control,
    handleSubmit,
    formState: {errors},
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {ownerName, email},
  });

  const onNext = data => {
    setField('ownerName', data.ownerName.trim());
    setField('email', data.email.trim().toLowerCase());
    navigation.navigate('RegisterStep2');
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled">
        <StepIndicator currentStep={1} />

        <Text style={styles.title}>{t('registration.createYourAccount')}</Text>
        <Text style={styles.subtitle}>
          {t('auth.ownerName')} & {t('auth.email')}
        </Text>

        <View style={styles.form}>
          <Controller
            control={control}
            name="ownerName"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('auth.ownerName')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={!!errors.ownerName}
                mode="outlined"
                style={styles.input}
                outlineColor={onboardingColors.dot}
                activeOutlineColor={onboardingColors.primary}
              />
            )}
          />
          {errors.ownerName && (
            <Text style={styles.errorText}>{errors.ownerName.message}</Text>
          )}

          <Controller
            control={control}
            name="email"
            render={({field: {onChange, onBlur, value}}) => (
              <TextInput
                label={t('auth.email')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                error={!!errors.email}
                keyboardType="email-address"
                autoCapitalize="none"
                mode="outlined"
                style={styles.input}
                outlineColor={onboardingColors.dot}
                activeOutlineColor={onboardingColors.primary}
              />
            )}
          />
          {errors.email && (
            <Text style={styles.errorText}>{errors.email.message}</Text>
          )}
        </View>

        <TouchableOpacity style={styles.nextButton} onPress={handleSubmit(onNext)}>
          <Text style={styles.nextButtonText}>{t('common.next')}</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.loginLink}
          onPress={() => navigation.navigate('Login')}>
          <Text style={styles.loginLinkText}>
            {t('auth.alreadyHaveAccount')}{' '}
            <Text style={styles.loginLinkBold}>{t('auth.login')}</Text>
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: onboardingColors.background,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: 32,
    paddingTop: 60,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: onboardingColors.text,
    textAlign: 'center',
    marginTop: 16,
  },
  subtitle: {
    fontSize: 14,
    color: onboardingColors.textSecondary,
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 32,
  },
  form: {
    marginBottom: 24,
  },
  input: {
    marginBottom: 12,
    backgroundColor: onboardingColors.white,
  },
  errorText: {
    color: '#D32F2F',
    fontSize: 12,
    marginTop: -8,
    marginBottom: 8,
    marginLeft: 4,
  },
  nextButton: {
    backgroundColor: onboardingColors.primary,
    borderRadius: 28,
    paddingVertical: 14,
    alignItems: 'center',
  },
  nextButtonText: {
    color: onboardingColors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  loginLink: {
    marginTop: 24,
    alignItems: 'center',
  },
  loginLinkText: {
    fontSize: 14,
    color: onboardingColors.textSecondary,
  },
  loginLinkBold: {
    color: onboardingColors.primary,
    fontWeight: '600',
  },
});

export default RegisterStep1Screen;
